/**
 * Processing API - Job queue and processing run management.
 */

import apiClient, { type RequestConfig } from './client';
import type {
  ProcessingBudgetConfig,
  ProcessingPipelineOptions,
  ProcessingPipelineResponse,
  ProcessingPipelineStatus,
  ProcessingRun,
  RunEvent,
  PaginatedResponse,
  PaginationParams,
} from './types';

const ENDPOINT = '/processing';

export const processingApi = {
  /**
   * Start a graph-native processing pipeline for a matter.
   */
  async startPipeline(
    matterId: string,
    request: {
      preflight_id: string;
      options: ProcessingPipelineOptions;
      budget: ProcessingBudgetConfig;
      priority?: 'high' | 'default' | 'low';
      accept_estimated_cost?: boolean;
      max_estimated_cost_usd?: number;
    },
    config?: RequestConfig
  ): Promise<ProcessingPipelineResponse> {
    const { data } = await apiClient.post<ProcessingPipelineResponse>(
      `${ENDPOINT}/matters/${matterId}/pipelines`,
      request,
      config
    );
    return data;
  },

  /**
   * Get the current status for a processing pipeline.
   */
  async getPipelineStatus(id: string, config?: RequestConfig): Promise<ProcessingPipelineStatus> {
    const { data } = await apiClient.get<ProcessingPipelineStatus>(`${ENDPOINT}/pipelines/${id}`, config);
    return data;
  },

  /**
   * Pause a pipeline.
   */
  async pausePipeline(
    id: string,
    config?: RequestConfig
  ): Promise<{ pipeline_id: string; status: string; jobs_cancelled?: number }> {
    const { data } = await apiClient.post<{ pipeline_id: string; status: string; jobs_cancelled?: number }>(
      `${ENDPOINT}/pipelines/${id}/pause`,
      null,
      config
    );
    return data;
  },

  /**
   * Resume a pipeline.
   */
  async resumePipeline(
    id: string,
    config?: RequestConfig
  ): Promise<{ pipeline_id: string; status: string; jobs_requeued?: number }> {
    const { data } = await apiClient.post<{ pipeline_id: string; status: string; jobs_requeued?: number }>(
      `${ENDPOINT}/pipelines/${id}/resume`,
      null,
      config
    );
    return data;
  },

  /**
   * Get pipeline cost.
   */
  async getPipelineCost(
    id: string,
    config?: RequestConfig
  ): Promise<{ pipeline_id: string; total_cost: number; breakdown: Record<string, number>; runs_completed: number }> {
    const { data } = await apiClient.get<{
      pipeline_id: string;
      total_cost: number;
      breakdown: Record<string, number>;
      runs_completed: number;
    }>(`${ENDPOINT}/pipelines/${id}/cost`, config);
    return data;
  },

  /**
   * Get a processing run by ID.
   */
  async get(id: string, config?: RequestConfig): Promise<ProcessingRun> {
    const { data } = await apiClient.get<ProcessingRun>(`${ENDPOINT}/runs/${id}`, config);
    return data;
  },

  /**
   * List processing runs for a matter.
   */
  async listForMatter(
    matterId: string,
    params?: PaginationParams & {
      type?: string;
      status?: string;
    },
    config?: RequestConfig
  ): Promise<PaginatedResponse<ProcessingRun>> {
    const { data } = await apiClient.get<PaginatedResponse<ProcessingRun>>(
      `${ENDPOINT}/matters/${matterId}/runs`,
      {
        ...config,
        params: params as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Get active (running/queued) processing runs for a matter.
   */
  async getActive(matterId: string, config?: RequestConfig): Promise<ProcessingRun[]> {
    const { data } = await apiClient.get<ProcessingRun[]>(
      `${ENDPOINT}/matters/${matterId}/active`,
      config
    );
    return data;
  },

  /**
   * Cancel a processing run.
   */
  async cancel(id: string, config?: RequestConfig): Promise<ProcessingRun> {
    const { data } = await apiClient.post<ProcessingRun>(`${ENDPOINT}/runs/${id}/cancel`, null, config);
    return data;
  },

  /**
   * Retry a failed processing run.
   */
  async retry(id: string, config?: RequestConfig): Promise<{
    run_id: string;
    job_id: string | null;
    status: string;
    message?: string;
  }> {
    const { data } = await apiClient.post<{
      run_id: string;
      job_id: string | null;
      status: string;
      message?: string;
    }>(`${ENDPOINT}/runs/${id}/retry`, null, config);
    return data;
  },

  /**
   * Get events for a processing run.
   */
  async getEvents(
    id: string,
    params?: {
      event_type?: string;
      limit?: number;
      offset?: number;
    },
    config?: RequestConfig
  ): Promise<RunEvent[]> {
    const { data } = await apiClient.get<{ items: RunEvent[]; total: number } | RunEvent[]>(
      `${ENDPOINT}/runs/${id}/events`,
      {
        ...config,
        params: params as Record<string, string | number | boolean | undefined>,
      }
    );
    // Backend returns { items, total } but callers expect a plain array
    return Array.isArray(data) ? data : data.items;
  },

  /**
   * Get queue statistics.
   */
  async getQueueStats(config?: RequestConfig): Promise<{
    queues: Record<string, {
      pending: number;
      failed: number;
      scheduled: number;
      started: number;
      finished: number;
    }>;
    workers: number;
    total_pending: number;
    total_failed: number;
  }> {
    const { data } = await apiClient.get<{
      queues: Record<string, {
        pending: number;
        failed: number;
        scheduled: number;
        started: number;
        finished: number;
      }>;
      workers: number;
      total_pending: number;
      total_failed: number;
    }>(`${ENDPOINT}/queue-stats`, config);
    return data;
  },

  /**
   * Subscribe to processing updates via SSE.
   */
  subscribeToRun(
    id: string,
    handlers: {
      onProgress?: (event: RunEvent) => void;
      onMilestone?: (event: RunEvent) => void;
      onError?: (event: RunEvent) => void;
      onComplete?: (run: ProcessingRun) => void;
      onConnectionError?: (error: Event) => void;
    }
  ): EventSource {
    const eventSource = apiClient.sse(`/stream/processing/${id}`, {
      onMessage: (event) => {
        let data: ({ event_type?: string; run?: ProcessingRun } & Record<string, unknown>) | null = null;
        try {
          data = JSON.parse(event.data) as ({ event_type?: string; run?: ProcessingRun } & Record<string, unknown>);
        } catch {
          return; // Skip malformed SSE frames
        }
        switch (data.event_type) {
          case 'progress':
            handlers.onProgress?.(data as unknown as RunEvent);
            break;
          case 'milestone':
            handlers.onMilestone?.(data as unknown as RunEvent);
            break;
          case 'error':
            handlers.onError?.(data as unknown as RunEvent);
            break;
          case 'complete':
            if (data.run) {
              handlers.onComplete?.(data.run);
            }
            break;
        }
      },
      onError: handlers.onConnectionError,
    });
    return eventSource;
  },

  /**
   * Subscribe to all processing updates for a matter.
   */
  subscribeToMatter(
    matterId: string,
    handlers: {
      onRunStarted?: (run: ProcessingRun) => void;
      onRunProgress?: (run: ProcessingRun) => void;
      onRunCompleted?: (run: ProcessingRun) => void;
      onRunFailed?: (run: ProcessingRun) => void;
      onError?: (error: Event) => void;
    }
  ): EventSource {
    const eventSource = apiClient.sse(`/stream/matter/${matterId}/processing`, {
      onMessage: (event) => {
        let data: ({ type?: string; run?: ProcessingRun } & Record<string, unknown>) | null = null;
        try {
          data = JSON.parse(event.data) as ({ type?: string; run?: ProcessingRun } & Record<string, unknown>);
        } catch {
          return; // Skip malformed SSE frames
        }
        switch (data.type) {
          case 'run_started':
            if (data.run) {
              handlers.onRunStarted?.(data.run);
            }
            break;
          case 'run_progress':
            if (data.run) {
              handlers.onRunProgress?.(data.run);
            }
            break;
          case 'run_completed':
            if (data.run) {
              handlers.onRunCompleted?.(data.run);
            }
            break;
          case 'run_failed':
            if (data.run) {
              handlers.onRunFailed?.(data.run);
            }
            break;
        }
      },
      onError: handlers.onError,
    });
    return eventSource;
  },
};

export default processingApi;
